import { Bill } from '@enview/interface/types/bills/Bill';
import React, { ReactElement, useMemo, useRef, useState } from 'react';
import RichTextEditor from '../ActivityFeed/CommentForm/RichTextEditor';
import { Descendant } from 'slate';
import { CustomFieldsAPI } from '../../../api';
import { getOrgForTeamMode, getSelectedTeamIds, getTeamMode } from '../../../dux';
import { useSelector } from 'react-redux';
import {
  getTeamsWithBillFieldValue,
  lookupCustomFieldValue,
} from '../../../helpers/BillHelper';
import { UserAction } from '@enview/interface/types/actions/UserAction';
import ConfirmActionPopover, {
  PopoverConfirmProps,
} from '../Popovers/ConfirmActionPopover';
import TeamSelector from '../TeamSelector';
import CustomField from '@enview/interface/types/CustomFields';
import MemoIcon from '../Icons/MemoIcon';
import SingleSelectDropdown, {
  OptionType,
} from '../../Shared/DropDowns/SingleSelectDropDown';
import { buildColumnOptions } from '../../../utils';
import { CUSTOM_FIELD_RICH_TEXT_CHAR_LIMIT } from '../../../utils/constants';

interface BillDetailedCustomFieldModalProps extends Partial<PopoverConfirmProps> {
  bill: Bill;
  target: ReactElement;
  show: boolean;
  toggleCallback: (evt: React.SyntheticEvent) => void;
  closePopup: () => void;
  customFields: CustomField[];
  activeField?: string;
  setActiveField?: (field: string) => void;
}

const CONFRIM_POPOVER_TITLE = 'Select the teams you want to share with:';

export const BillDetailedCustomFieldModal: React.FC<
  BillDetailedCustomFieldModalProps
> = ({
  bill,
  closePopup,
  show,
  target,
  customFields,
  toggleCallback,
  activeField,
  setActiveField,
}) => {
  const nodeRef = useRef<any>();
  const [comment, setComment] = useState<any>('');
  const [displaySavePopup, setDisplaySavePopup] = useState(false);
  const [shouldArchive, setShouldArchive] = useState(false);

  const organization = useSelector(getOrgForTeamMode);
  const teamModeId = useSelector(getTeamMode);
  const selectedTeamIds = useSelector(getSelectedTeamIds);

  const [upsertCustomFieldValue] =
    CustomFieldsAPI.endpoints.upsertCustomFieldValue.useMutation();

  const orgFieldOptions = useMemo(
    () => buildColumnOptions(customFields, true),
    [customFields],
  );

  const onClose = () => {
    setDisplaySavePopup(false);
    closePopup();
  };
  const onChangeToEditor = (event: Descendant[]) => {
    setComment(event);
  };

  const handleFieldSelection = (option: OptionType<string>) => {
    if (!setActiveField) return;
    setActiveField(option.value);
  };

  const billValue = useMemo(() => {
    if (!activeField || !organization) return {};
    return lookupCustomFieldValue(
      bill.customFields ?? {},
      activeField,
      organization.id,
      teamModeId,
    );
  }, [activeField, bill, organization, teamModeId]);

  const teamsWithBillFieldValue = useMemo(() => {
    if (!activeField || !organization) return [];
    return getTeamsWithBillFieldValue(
      bill.customFields ?? {},
      activeField,
      organization.id,
      billValue?.id,
    );
  }, [activeField, billValue?.id, bill, organization]);

  const hasValueSet = !!billValue?.fieldValue;

  const onPostConfirm = async () => {
    const activeCustomField = customFields.find(
      (customField) => customField.title === activeField,
    );

    if (!organization?.id || !activeCustomField) return;

    const update = {
      fieldId: activeCustomField.id,
      billId: bill.id,
      data: {
        teams: selectedTeamIds,
        value: {
          richText: { children: comment },
        },
        orgId: organization.id,
        shouldArchive,
        ...(hasValueSet && billValue?.id ? { valueId: billValue.id } : {}),
      },
    };

    await upsertCustomFieldValue(update);

    setDisplaySavePopup(false);
    setShouldArchive(false);
    closePopup();
  };

  const onSaveClicked = () => {
    // TODO: add feature gating
    if (!comment && typeof comment !== 'object') return;
    setDisplaySavePopup(true);
  };

  const saveButton = (
    <ConfirmActionPopover
      align="end"
      closePopup={() => setDisplaySavePopup(false)}
      content={
        <div className="post-confirmation">
          <p className="popover-confirm-body">{CONFRIM_POPOVER_TITLE}</p>
          <TeamSelector initialSelection={teamsWithBillFieldValue} />
          <div className="text-center mt-3">
            <button
              className="btn btn-sm btn-block btn-action"
              onClick={onPostConfirm}
              type="button"
            >
              Save
            </button>
          </div>
        </div>
      }
      positions={['bottom']}
      show={displaySavePopup}
      target={
        <div className="action-buttons">
          <button
            className="btn btn-link btn-sm cf-btn mr-2"
            disabled={!comment}
            onClick={() => {
              setShouldArchive(true);
              onSaveClicked();
            }}
            type="button"
          >
            Delete
          </button>

          <button
            className="btn btn-primary btn-sm cf-btn"
            disabled={!comment}
            onClick={onSaveClicked}
            type="button"
          >
            Save
          </button>
        </div>
      }
      targetStyle={{ width: '100%' }}
      toggleCallback={() => {}}
    />
  );

  const getUserAction = useMemo(() => {
    if (!billValue?.fieldValue) return;
    const { richText } = billValue?.fieldValue;
    if (!richText) return;

    return {
      commentJson: billValue?.fieldValue?.richText,
    } as Partial<UserAction>;
  }, [activeField, billValue?.fieldValue]);

  const content = (
    <div className="cf-popover-content">
      <div className="my-3 field-select-row">
        <div className="memo-icon-wrapper mr-2">
          <MemoIcon />
        </div>
        <div className="flex-grow-1">
          <SingleSelectDropdown
            defaultLabel="Select a custom field"
            onSelect={handleFieldSelection}
            options={orgFieldOptions}
            selectedValue={activeField}
          />
        </div>
      </div>

      <div className="rich-text-editor mt-2" id="cf-rich-text" ref={nodeRef}>
        <RichTextEditor
          charLimit={CUSTOM_FIELD_RICH_TEXT_CHAR_LIMIT}
          key={`${activeField}-${billValue?.id}`}
          onChangeToEditor={onChangeToEditor}
          placeholder={''}
          style={{ padding: '12px', maxHeight: '180px', overflowY: 'auto' }}
          userAction={getUserAction}
        />
        <div className="d-flex flex-row my-2 ml-auto">{saveButton}</div>
      </div>
    </div>
  );

  return (
    <ConfirmActionPopover
      closePopup={onClose}
      content={content}
      show={show}
      target={target}
      toggleCallback={toggleCallback}
    />
  );
};

export default BillDetailedCustomFieldModal;
